import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import {
  AssignmentRequestReasonEnum,
  AssignmentRequestStatusEnum,
  AssignmentTypeEnum,
  CompanyModel,
  ExtendedAssignmentRequestModel,
} from 'api/model';
import Modal from 'components/Modal';
import KeyValueList, { KeyListRow } from 'components/KeyValueList';
import Input from 'components/inputs/Input';
import {
  formatDateTime,
  getHoursAndMinutesFromMillisecondsString,
  toInputDateTimeString,
} from 'utils/date-helpers';
import PrioritizedCaseAssignmentUserPicker from 'components/PrioritizedCaseAssignmentUserPicker';
import AutoSizedTextArea from 'components/inputs/AutoSizedTextArea';
import { arraySpreadIf } from 'utils/spreading';
import AssignmentRequestAssignmentPicker from './AssignmentRequestAssignmentPicker';
import AssignmentRequestEvents from './AssignmentRequestEvents';
import SectionHeader from 'components/SectionHeader';
import TextButton from 'components/inputs/TextButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink, faPhone } from '@fortawesome/free-solid-svg-icons';
import LoadingSpinner from 'components/spinners/LoadingSpinner';
import AssignmentRequestActions from './AssignmentRequestActions';
import CompanyPicker, {
  StyledCompanySelectItem,
} from 'components/CompanyPicker';
import { SelectOption } from 'components/inputs/SearchableSelect';
import Select from 'components/inputs/Select';
import useAssignmentRequestReasonSelectOptions from './useAssignmentRequestReasonOptions';
import useAssignmentRequestForm, {
  getDefaultRequestValues,
} from './useAssignmentRequestForm';
import {
  useAssignmentRequestsCreateAssignmentRequest,
  useAssignmentRequestsGetNumberOfAvailableAssignmentsPerToCompanyLocation,
  useAssignmentRequestsUpdateAssignmentRequest,
} from 'api/assignment-requests/assignment-requests';
import Roles from 'constants/Roles';
import FacilityLockWarning, {
  useToCompanyLock,
} from 'components/AssignmentRequests/FacilityLockWarning';
import useTranslations from 'contexts/basicData/useTranslations';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 10px;
`;

const Horizontal = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
`;

const ExtraInfoInput = styled(AutoSizedTextArea)`
  resize: vertical;
  width: 395px;
  max-width: 395px;
`;

const StyledSelect = styled(Select)`
  width: 395px;
  max-width: 395px;
`;

const NumberOfCarsInput = styled(Input)`
  width: 50px;
`;

const EventSection = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 250px;
  width: 250px;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const FormContent = styled.div`
  display: flex;
  flex-direction: row;
`;

const StyledKVList = styled(KeyValueList)`
  margin-top: 5px;
  flex: 1;
`;

const NoWrap = styled.span<{ tint?: boolean }>`
  white-space: nowrap;
  ${({ tint, theme }) =>
    tint &&
    `
    color: ${theme.colors.foreground.tint};
  `}
`;

const StyledLink = styled.a<{ tint?: boolean }>`
  ${({ tint, theme }) =>
    tint &&
    `
    color: ${theme.colors.foreground.tint} !important;
    text-decoration: none; // Adjust as needed
  `}
`;

interface Props {
  request?: ExtendedAssignmentRequestModel;
  onClose?: () => void;
  onUpdated?: () => void;
}

const AssignmentRequestModal: React.FC<Props> = ({
  onClose,
  onUpdated,
  request,
}) => {
  const { assignmentRequestStatuses } = useTranslations();
  // api calls
  const createAssignmentRequest =
    useAssignmentRequestsCreateAssignmentRequest();
  const updateAssignmentRequest =
    useAssignmentRequestsUpdateAssignmentRequest();

  const requestLoading = useMemo(
    () =>
      createAssignmentRequest.isLoading || updateAssignmentRequest.isLoading,
    [createAssignmentRequest.isLoading, updateAssignmentRequest.isLoading]
  );
  const { assignmentRequest, assignmentRequestEvents } = request ?? {};

  // form
  const assignmentRequestForm = useAssignmentRequestForm(assignmentRequest);

  const {
    setValue,
    watch,
    getValues,
    handleSubmit,
    formState: { isDirty },
  } = assignmentRequestForm.form;

  const {
    expiryTimeInMinutes,
    canEdit,
    isNew,
    isCreatedState,
    fromCompanyFilter,
    toCompanyFilter,
    selectedUserMessage,
    formReady,
    timeBetweenCompanies,
    setExpiryTimeInMinutes,
    needsExpiryInfo,
    expiryTimeInDate,
    hasOriginatingAssignment,
    isTruckTransport,
    selectedUser,
    canDriveCarTransportTrucks,
    carTransportTrucksList,
  } = assignmentRequestForm;

  // form dependent api calls
  const getNumberOfAssignmentsPerToLocationCall =
    useAssignmentRequestsGetNumberOfAvailableAssignmentsPerToCompanyLocation();

  // create and update
  const onCreate = handleSubmit(async (data) => {
    await createAssignmentRequest.mutateAsync({
      data,
    });
    onClose?.();
  });

  const onUpdate = handleSubmit(async (data) => {
    try {
      await updateAssignmentRequest.mutateAsync({
        data,
        params: {
          expires: expiryTimeInDate,
        },
      });
      onUpdated?.();
      onClose?.();
    } catch (e) {
      // console.error(e);
    }
  });

  const { allocatedAssignments, assignmentRequestReasonID } = watch();

  // Anläggningslåset: ny förfrågan till låst anläggning, eller byte av anläggning eller dag till
  // en låst, nekas. En förfrågan som redan går dit samma dag får redigeras som vanligt.
  const toCompanyLock = useToCompanyLock(
    watch('toCompanyID'),
    watch('targetStartTime'),
    {
      originalToCompanyId: assignmentRequest?.toCompanyID,
      originalTargetStartTime: assignmentRequest?.targetStartTime,
    }
  );

  const assignmentRequestReasonSelectOptions =
    useAssignmentRequestReasonSelectOptions(assignmentRequestReasonID);

  const renderToCompanyOption = useCallback(
    (
      key: string,
      option: SelectOption<number | null, CompanyModel | null>,
      onClick: () => void,
      isSelected: boolean
    ) => {
      return (
        <StyledCompanySelectItem
          isNoneItem={option.value === null}
          key={key}
          onClick={onClick}
          selected={isSelected}
        >
          {option.label}
          {option.value !== null &&
            getNumberOfAssignmentsPerToLocationCall.data?.[option.value] !==
              undefined &&
            ` (${getNumberOfAssignmentsPerToLocationCall.data[option.value]})`}
        </StyledCompanySelectItem>
      );
    },
    [getNumberOfAssignmentsPerToLocationCall.data]
  );

  const assignmentRows: KeyListRow[] = [
    ...arraySpreadIf(
      assignmentRequest?.originatingAssignmentID !== undefined &&
        assignmentRequest?.originatingAssignmentID !== null,
      {
        key: 'Följande uppdrag',
        value: (
          <>
            <TextButton
              disabled={!canEdit}
              onClick={() => {
                window.open(
                  `/sok/${assignmentRequest!.originatingCaseID}`,
                  assignmentRequest!.originatingCaseID?.toString()
                );
              }}
            >
              {`${assignmentRequest?.originatingCaseRegistrationNumber} `}
              <FontAwesomeIcon icon={faExternalLink} />
            </TextButton>
            <span>{`
        (Starttid: ${assignmentRequest?.originatingAssignment?.assignment.estimatedStartTime ? formatDateTime(assignmentRequest!.originatingAssignment.assignment.estimatedStartTime) : ''}) 
        `}</span>
          </>
        ),
      }
    ),
    ...arraySpreadIf(!!assignmentRequest, {
      key: 'Uppdrag',
      value: (
        <AssignmentRequestAssignmentPicker
          disabled={!canEdit}
          onSelected={(assignments) => {
            setValue('allocatedAssignments', assignments, {
              shouldDirty: true,
            });
          }}
          selected={allocatedAssignments}
          request={getValues()!}
          defaultSelected={
            getDefaultRequestValues(request?.assignmentRequest)
              .allocatedAssignments
          }
        />
      ),
    }),
    ...arraySpreadIf(!!assignmentRequest, {
      key: 'Status',
      value: assignmentRequest
        ? assignmentRequestStatuses[
            assignmentRequest?.assignmentRequestStatusID
          ]
        : '-',
    }),
  ];

  const standardRows: KeyListRow[] = [
    {
      key: 'Hämtas tid',
      value: (
        <>
          <Input
            disabled={!canEdit}
            onChange={(e) =>
              setValue('targetStartTime', new Date(e.currentTarget.value), {
                shouldDirty: true,
              })
            }
            small
            type="datetime-local"
            value={toInputDateTimeString(watch('targetStartTime'))}
          />
          <span>
            {((request?.assignmentRequest.originatingAssignment &&
              request?.assignmentRequest.originatingAssignment?.assignment
                .estimatedStartTime) ||
              request?.assignmentRequest.originatingAssignment?.assignment
                .bookedTo) &&
              toInputDateTimeString(
                new Date(
                  request?.assignmentRequest.originatingAssignment!.assignment
                    .estimatedStartTime ??
                    request?.assignmentRequest.originatingAssignment!.assignment
                      .bookedTo
                )
              )}
          </span>
        </>
      ),
    },
    {
      key: 'Från anläggning',
      value: (
        <Horizontal>
          <CompanyPicker
            disabled={!canEdit}
            filter={fromCompanyFilter}
            selectedCompanyId={watch('fromCompanyID')}
            onCompanySelected={(company: CompanyModel | null) => {
              if (company) {
                setValue('fromCompanyID', company.companyID!, {
                  shouldDirty: true,
                });
              }
            }}
          />
        </Horizontal>
      ),
    },
    {
      key: 'Till anläggning',
      value: (
        <Horizontal>
          <CompanyPicker
            disabled={!canEdit}
            filter={toCompanyFilter}
            selectedCompanyId={watch('toCompanyID')}
            renderItem={renderToCompanyOption}
            onCompanySelected={(company: CompanyModel | null) => {
              if (company) {
                setValue('toCompanyID', company.companyID!, {
                  shouldDirty: true,
                });
              }
            }}
          />
          <FacilityLockWarning lock={toCompanyLock.lock} />
          {timeBetweenCompanies !== undefined && (
            <div>
              Fast tid:{' '}
              {getHoursAndMinutesFromMillisecondsString(
                timeBetweenCompanies ?? 0
              )}
            </div>
          )}
        </Horizontal>
      ),
    },
    {
      key: 'Fälttestare',
      value: (
        <Horizontal>
          <PrioritizedCaseAssignmentUserPicker
            disabled={
              !canEdit ||
              (!isNew && !isCreatedState) ||
              hasOriginatingAssignment
            }
            caseModel={{}}
            bookedTo={
              watch('targetStartTime')
                ? toInputDateTimeString(watch('targetStartTime'))
                : ''
            }
            selectedUserId={watch('requestedForID') ?? null}
            estimatedStartTime={
              watch('targetStartTime')
                ? toInputDateTimeString(watch('targetStartTime'))
                : ''
            }
            estimatedDuration={timeBetweenCompanies ?? 0}
            assignmentType={AssignmentTypeEnum.Normal}
            onUserPicked={(userModel) => {
              setValue('requestedForID', userModel?.userID, {
                shouldDirty: true,
              });
            }}
            assignmentId={-1}
            requireExistingAssignmentToSelectFT={false}
          />
          {watch('requestedForID') !== undefined && (
            <>
              <NoWrap>{selectedUserMessage}</NoWrap>
              {!selectedUser?.phoneNumber ? null : (
                <NoWrap tint>
                  <StyledLink href={`tel:${selectedUser?.phoneNumber}`}>
                    <FontAwesomeIcon icon={faPhone} />{' '}
                    {selectedUser?.phoneNumber}
                  </StyledLink>
                </NoWrap>
              )}
            </>
          )}
        </Horizontal>
      ),
    },
    ...arraySpreadIf(!!canDriveCarTransportTrucks, {
      key: 'Välj lastbil',
      value: (
        <Select
          disabled={!canEdit}
          value={watch('carTransportTruckID')}
          onChange={(eve) => {
            setValue('carTransportTruckID', Number(eve.currentTarget.value), {
              shouldDirty: true,
            });
          }}
        >
          <option value={undefined}>Välj lastbil</option>
          {carTransportTrucksList.map((truck) => (
            <option key={truck.id} value={truck.id}>
              {truck.registrationNumber} (plats för{' '}
              {`${truck.maxNumberOfCars} bilar`})
            </option>
          ))}
        </Select>
      ),
    }),
    {
      key: 'Antal bilar',
      value: (
        <Horizontal>
          <NumberOfCarsInput
            disabled={!canEdit || !isTruckTransport}
            type="number"
            value={watch('numberOfCars')}
            onChange={(e) =>
              setValue('numberOfCars', Number(e.currentTarget.value), {
                shouldDirty: true,
              })
            }
          />
          (Endast för lastbil)
        </Horizontal>
      ),
    },
    {
      key: 'Anledning',
      value: (
        <StyledSelect
          value={assignmentRequestReasonID}
          onChange={(eve) => {
            setValue(
              'assignmentRequestReasonID',
              Number(eve.currentTarget.value) as AssignmentRequestReasonEnum
            );
          }}
        >
          {assignmentRequestReasonSelectOptions.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </StyledSelect>
      ),
    },
    {
      key: 'Extra info',
      value: (
        <ExtraInfoInput
          disabled={!canEdit}
          onChange={(e) =>
            setValue('extraInfo', e.currentTarget.value, { shouldDirty: true })
          }
          value={watch('extraInfo')}
        />
      ),
    },
    ...arraySpreadIf(needsExpiryInfo, {
      key: 'Acceptera inom',
      value: (
        <>
          <Input
            style={{ width: 60 }}
            disabled={!canEdit}
            onChange={(e) => {
              setExpiryTimeInMinutes(Number(e.currentTarget.value));
            }}
            small
            type="number"
            value={expiryTimeInMinutes}
            min={0}
            max={24 * 60}
          />
          <span> minuter ({formatDateTime(expiryTimeInDate)})</span>
        </>
      ),
    }),
  ];

  return (
    <Modal
      buttons={
        assignmentRequest?.assignmentRequestStatusID !==
        AssignmentRequestStatusEnum.Denied
          ? [
              {
                onClick:
                  assignmentRequest?.requestID &&
                  assignmentRequest?.requestID > 0
                    ? onUpdate
                    : onCreate,
                disabled:
                  !isDirty ||
                  !watch('requestedForID') ||
                  !watch('toCompanyID') ||
                  toCompanyLock.isLocked ||
                  requestLoading ||
                  !canEdit,
                label: 'Spara',
                roles: [Roles.Admin, Roles.AnnonsAdmin],
              },
              {
                onClick: onClose,
                disabled: requestLoading,
                label: 'Stäng',
                roles: [Roles.Admin, Roles.AnnonsAdmin],
              },
            ]
          : []
      }
      title={request ? 'Redigera förfrågan/bokning' : 'Ny förfrågan/bokning'}
      onClose={onClose}
    >
      <Wrapper>
        {formReady ? (
          <LoadingSpinner />
        ) : (
          <>
            <EventSection>
              <SectionHeader>Händelser</SectionHeader>
              <AssignmentRequestEvents
                events={assignmentRequestEvents ?? []}
                assignmentRequestId={assignmentRequest?.requestID}
                onUpdate={() => {
                  onUpdated?.();
                }}
              />
            </EventSection>
            <FormSection>
              <SectionHeader>Information</SectionHeader>
              <FormContent>
                <StyledKVList
                  colonKey
                  keyWidth="100"
                  rows={[...assignmentRows, ...standardRows]}
                />
                <AssignmentRequestActions
                  request={request}
                  onActionComplete={(close: boolean) => {
                    onUpdated?.();
                    if (close) {
                      onClose?.();
                    }
                  }}
                />
              </FormContent>
            </FormSection>
          </>
        )}
      </Wrapper>
    </Modal>
  );
};

export default AssignmentRequestModal;
